import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useState } from 'react';
import { Key, scopedKey } from '../../utils/asyncStorage';
import { safeMarkDevFlag } from '../../utils/safeMarkDevFlag';

export const useKeepsOnlyPrimaryOnboardingFlowsDebugFlag = () => {
  return useDebugFlag('keepOnlyPrimaryOnboardingFlows', false);
};

export const useEnablePasswordSignInDebugFlag = () => {
  return useDebugFlag('enablePasswordSigninMethod', false);
};

export const useDebugFlag = <T>(
  name: string,
  defaultValue?: T,
): [T | undefined, (value: T | undefined) => Promise<void>] => {
  const [flag, setFlag] = useState<T>();
  const key = `debug.flags.${name}`;

  useEffect(() => {
    AsyncStorage.getItem(key)
      .then(s => {
        if (s === null || s === undefined) {
          if (defaultValue !== undefined) {
            setFlag(defaultValue);
          }
        } else {
          const obj = JSON.parse(s) as { value: T };
          setFlag(obj.value);
        }
      })
      .catch(() => null);
  }, [key, defaultValue]);

  const updateFlag = useCallback(
    async (value: T | null | undefined) => {
      if (value === null || value === undefined) {
        await AsyncStorage.removeItem(key);
        setFlag(undefined);
      } else {
        await AsyncStorage.setItem(key, JSON.stringify({ value }));
        setFlag(value);
      }
    },
    [key],
  );

  return [flag, updateFlag];
};

const asyncStorageKeysToPersist: Set<string> = new Set(
  [
    Key.NewOnboardingHasCompletion,
    Key.NewOnboardingProgress,
    Key.PlayedIntroVideo,
    Key.DailyLaunchCount,
    Key.AndroidNotificationPermissionStatus,
    Key.AnonymousVideoFeedsSessionId,
  ].map(x => scopedKey(x)),
);

const kDebugKeyRemoval = safeMarkDevFlag(false);

export async function clearAsyncStorageExceptDebugFlags() {
  const keys = await AsyncStorage.getAllKeys();

  const keysToRemove = keys.filter(x => {
    if (kDebugKeyRemoval) {
      console.log({
        key: x,
        isDebugFlag: x.startsWith('debug.flags.'),
        shouldPersist: asyncStorageKeysToPersist.has(x),
        willRemove:
          !x.startsWith('debug.flags.') && !asyncStorageKeysToPersist.has(x),
      });
    }
    return !x.startsWith('debug.flags.') && !asyncStorageKeysToPersist.has(x);
  });

  if (kDebugKeyRemoval) {
    console.log('will remove async storage keys: ', keysToRemove);
  }

  await AsyncStorage.multiRemove(keysToRemove);
}
